import { AnalyticsIcon } from '@assets/icons/AnalyticsIcon';
import { AssignmentIcon } from '@assets/icons/AssignmentIcon';
import DataLayersIcon from '@assets/icons/minor/DataLayersIcon';
import MapIcon from '@assets/icons/minor/MapIcon';
import type { ReactElement } from 'react';
import type { NavigateFunction } from 'react-router';
import type { Dispatch } from 'redux';
import {
  DashboardIcon,
  MyCropwiseIcon,
  ProductivityMapsIcon,
  StickyNoteIcon,
  YieldAnalysisIcon,
} from '../../assets/icons/minor';
import { SupportedEntitlements } from '../../core/models/entitlements';
import { Actions } from '../../redux/actions';
import { routePaths } from '../../screens/routes';

interface InnerProps {
  dispatch: Dispatch;
  sidebarCollapsed: boolean;
}

export interface MenuRouteParams {
  organizationId?: string;
  propertyId?: string;
  workspaceId?: string;
}

export interface MenuRoute {
  id?: string;
  path: string;
  label: string;
  amplitudeName: string;
  icon: ReactElement;
  isEndRoute?: boolean;
  entitlements?: SupportedEntitlements[];
  navigate?: (
    params: MenuRouteParams,
    navigate: NavigateFunction,
    navigateParams?: InnerProps
  ) => void;
}

export const getSidebarOptions: MenuRoute[] = [
  {
    id: 'sidebar-dashboard',
    path: routePaths.dashboard,
    label: 'sidebar.dashboard',
    amplitudeName: 'Dashboard',
    icon: <DashboardIcon />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/dashboard`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
  },
  {
    path: routePaths.view,
    label: 'sidebar.image_catalog',
    amplitudeName: 'Map View',
    icon: <MapIcon width={24} height={24} />,
    isEndRoute: true,
    navigate: ({ organizationId, propertyId }, navigate) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}`);
    },
    entitlements: [],
  },
  {
    path: routePaths.analytics,
    label: 'sidebar.analytics',
    amplitudeName: 'Analytics',
    icon: <AnalyticsIcon />,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/analytics`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [],
  },
  {
    path: routePaths.notes,
    label: 'sidebar.notes',
    amplitudeName: 'Notes',
    icon: <StickyNoteIcon />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/notes`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [SupportedEntitlements.NOTES],
  },
  {
    path: routePaths.prescriptions,
    label: 'sidebar.prescriptions',
    amplitudeName: 'Prescriptions',
    icon: <AssignmentIcon fill="#696F88" />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/prescriptions`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [
      SupportedEntitlements.PRESCRIPTIONS,
      SupportedEntitlements.PRESCRIPTIONS_CROP_PROTECTION,
      SupportedEntitlements.PRESCRIPTIONS_FERTILIZER,
      SupportedEntitlements.PRESCRIPTIONS_SEEDING,
    ],
  },
  {
    path: routePaths.productivityMaps,
    label: 'sidebar.productivity_maps',
    amplitudeName: 'Productivity Maps',
    icon: <ProductivityMapsIcon />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/productivity`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [SupportedEntitlements.PRODUCTIVITY_MAP],
  },
  {
    path: routePaths.yieldAnalysis,
    label: 'sidebar.yield_analysis',
    amplitudeName: 'Yield Analysis',
    icon: <YieldAnalysisIcon />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/yield-analysis`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [SupportedEntitlements.YIELD_ANALYSIS],
  },
  {
    path: routePaths.dataLayers,
    label: 'sidebar.data_layers',
    amplitudeName: 'Data Layers',
    icon: <DataLayersIcon />,
    isEndRoute: false,
    navigate: ({ organizationId, propertyId }, navigate, params) => {
      navigate(`/app/organizations/${organizationId}/properties/${propertyId}/data-layers`);
      params?.dispatch(Actions.setFirstLoadEvent());
    },
    entitlements: [SupportedEntitlements.DATA_LAYERS],
  },
];

export const workspaceSidebarOptions: MenuRoute[] = [
  {
    // matches the whole entities-hall branch, not just the workspace URL
    path: routePaths.organizations,
    label: 'sidebar.my_cropwise',
    amplitudeName: 'My Cropwise',
    icon: <MyCropwiseIcon />,
    isEndRoute: false,
    navigate: ({ workspaceId }, navigate) => {
      navigate(
        workspaceId ? `/app/organizations/workspace/${workspaceId}` : routePaths.organizations
      );
    },
  },
  {
    id: 'sidebar-dashboard',
    path: routePaths.workspaceDashboard,
    label: 'sidebar.dashboard',
    amplitudeName: 'Dashboard',
    icon: <DashboardIcon />,
    isEndRoute: true,
    navigate: ({ workspaceId }, navigate) => {
      navigate(`/app/organizations/workspace/${workspaceId}/dashboard`);
    },
  },
];
